import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import type { UserCredential } from 'firebase/auth';
import ENV from 'frontend-login/config/environment';
import type LoginController from 'frontend-login/controllers/login';
import { getHostDomain } from 'frontend-utils/utils/domain-checks';

interface QueryParams {
  idp?: string;
  isRedirecting?: boolean;
  next?: string;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
async function userIsAuthenticated() {
  const host = getHostDomain({ frontendGateway: ENV.frontendGateway });
  const userRequest = await fetch(`${host}/api/v3/users/?only_self=true`, {
    credentials: 'include',
  });

  return userRequest.ok;
}

export default class LoginRoute extends Route {
  queryParams = {
    next: {
      refreshModel: false,
    },
    idp: {
      refreshModel: false,
    },
    isRedirecting: {
      refreshModel: false,
    },
  };

  @service declare authentication: Services['authentication'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare sharedAuth: Services['shared-auth'];

  async beforeModel(): Promise<void> {
    if (await userIsAuthenticated()) {
      this.redirectToRoot();
      return;
    }

    this.authentication.handleRedirect().then(
      (result: UserCredential | null) =>
        this.sharedAuth.handleRedirectLogic(result, null),
      (error: unknown) => this.sharedAuth.handleRedirectLogic(null, error),
    );
  }

  redirectToRoot(): void {
    window.location.replace('/');
  }

  setupController(
    controller: LoginController,
    model: unknown,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);
    const queryParams: QueryParams = this.paramsFor(this.routeName);

    if (controller.email) {
      controller.sharedAuth.emailInputValue = controller.email;
    }

    // IDP (Identity Provider) is set in the URL when the user should be redirected to a specific IDP without showing the login page

    if (controller.idp) {
      controller.sharedAuth.redirectToIdp(controller.idp);
      controller.idp = null;
      this.router.replaceWith('login', { queryParams: { idp: null } });
    }

    // If the user stays for too long in the MFA screen, the login session will expire, and the user will be redirected to the login page
    if (controller.isLoginExpired) {
      controller.sharedAuth.showErrorMessage({
        bannerStyle: 'warning',
        message: this.intl.t('login.error.login_expired_description'),
        title: this.intl.t('login.error.login_expired_title'),
      });
    }

    if (controller.continueLogin) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      controller.sharedAuth.redirectToApp(controller.next);
    }

    if (queryParams.isRedirecting) {
      controller.sharedAuth.isRedirecting = true;
    }
  }
}
